import React, { useState } from 'react';
import axios from 'axios';
import {
  FaInfoCircle, FaTrash, FaBell, FaFilePdf, FaUnlock, FaHistory,
} from 'react-icons/fa';
import { formatDateForDisplay, formatDateForInput, renderReminderTracking, formSelectClass, formInputClass } from './menuTabUtils';

const AnnualReportTab = ({
  selectedMenuUser,
  handleAnnualReportServiceUpdate,
  handleRemoveAnnualReportService,
  handleAnnualReportStepStatusChange,
  handleReopenAnnualReportChecklist,
  handleAnnualReportPaymentStatusUpdate,
}) => {
  const [annualReportState, setAnnualReportState] = useState('');
  const [annualReportEntityType, setAnnualReportEntityType] = useState('');
  const [annualReportRegistrationDate, setAnnualReportRegistrationDate] = useState('');
  const [annualReportYearsToAdd, setAnnualReportYearsToAdd] = useState('');
  const [showEntityTypeSelect, setShowEntityTypeSelect] = useState(false);
  const [manualReminderLoading, setManualReminderLoading] = useState(false);

  const handleSendAnnualReportReminder = async (userId) => {
    if (!window.confirm('Send an annual report reminder email to this client now?')) return;
    setManualReminderLoading(true);
    try {
      const token = localStorage.getItem('employeeToken');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/send-immediate-annual-report-reminder`,
        { userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(response.data.message || 'Reminder email sent successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to send reminder email.');
    } finally {
      setManualReminderLoading(false);
    }
  };

  const state = annualReportState || selectedMenuUser?.annualReportService?.state;
  const entityType = annualReportEntityType || selectedMenuUser?.annualReportService?.entityType;
  const regDate = annualReportRegistrationDate || formatDateForInput(selectedMenuUser?.annualReportService?.registrationDate);

  return (
    <div className="space-y-4">
      <h5 className="font-bold text-gray-900 dark:text-white">Annual Report Service - Due Date Setup</h5>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select State</label>
        <select
          value={annualReportState || selectedMenuUser?.annualReportService?.state || ''}
          onChange={(e) => {
            setAnnualReportState(e.target.value);
            setShowEntityTypeSelect(e.target.value !== '');
            setAnnualReportEntityType('');
            setAnnualReportRegistrationDate('');
          }}
          className={formSelectClass}
        >
          <option value="">-- Select State --</option>
          {['Delaware', 'Wyoming', 'New Jersey', 'Florida', 'Pennsylvania', 'Washington'].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {(showEntityTypeSelect || selectedMenuUser?.annualReportService?.state) && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Entity Type</label>
          <select
            value={annualReportEntityType || selectedMenuUser?.annualReportService?.entityType || ''}
            onChange={(e) => setAnnualReportEntityType(e.target.value)}
            className={formSelectClass}
          >
            <option value="">-- Select Entity Type --</option>
            <option value="LLC">LLC</option>
            <option value="C-Corp">C-Corp</option>
          </select>
        </div>
      )}

      {entityType && (
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Registration/Formation Date</label>
            <input type="date" value={regDate} onChange={(e) => setAnnualReportRegistrationDate(e.target.value)} className={formInputClass} />
          </div>
          <div className="w-32">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Filing Year</label>
            <input
              type="number"
              placeholder="YYYY"
              value={annualReportYearsToAdd || selectedMenuUser?.annualReportService?.yearsToAdd || ''}
              onChange={(e) => setAnnualReportYearsToAdd(e.target.value)}
              className={formInputClass}
            />
          </div>
        </div>
      )}

      {state && (
        <div className="p-3 bg-gray-100 dark:bg-neutral-800 rounded-xl text-sm">
          <strong className="text-gray-900 dark:text-white">Due Date Rules for {state}:</strong>
          <ul className="mt-2 space-y-1 text-gray-600 dark:text-gray-400 list-disc list-inside">
            {state === 'Delaware' && (<><li><strong>LLC:</strong> Due Date is June 1st of the following year</li><li><strong>C-Corp:</strong> Due Date is March 1st of the following year</li></>)}
            {state === 'Wyoming' && <li>Due Date is the 1st day of the anniversary month of the following year</li>}
            {state === 'New Jersey' && <li>Due Date is the last day of the anniversary month of the following year</li>}
            {state === 'Florida' && <li>Due between January 1 - May 1 each year. Final deadline: May 1st (by 11:59 PM)</li>}
            {state === 'Pennsylvania' && (<><li><strong>LLC:</strong> Due Date is September 30th of the following year</li><li><strong>C-Corp:</strong> Due Date is June 30th of the following year</li></>)}
            {state === 'Washington' && <li>Due Date is the last day of the anniversary month of the following year</li>}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          const y = annualReportYearsToAdd !== '' ? annualReportYearsToAdd : (selectedMenuUser?.annualReportService?.yearsToAdd || 0);
          if (state && entityType && regDate) {
            handleAnnualReportServiceUpdate(selectedMenuUser._id, state, entityType, regDate, y);
            setAnnualReportState('');
            setAnnualReportEntityType('');
            setAnnualReportRegistrationDate('');
            setAnnualReportYearsToAdd('');
            setShowEntityTypeSelect(false);
          }
        }}
        disabled={!state || !entityType || !regDate}
        className="w-full px-4 py-2.5 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 disabled:opacity-50"
      >
        Save & Calculate Due Date
      </button>

      {selectedMenuUser?.annualReportService?.dueDate && (
        <div className="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 rounded-2xl border border-blue-100 dark:border-blue-800">
          <div className="flex items-center justify-between mb-3">
            <h6 className="font-bold text-blue-900 dark:text-blue-300 flex items-center gap-2">
              <FaInfoCircle className="text-blue-500" /> Current Annual Report Status
            </h6>
            <button
              type="button"
              onClick={() => handleRemoveAnnualReportService(selectedMenuUser._id)}
              className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-600 text-[10px] font-bold rounded-lg transition-colors flex items-center gap-1.5"
            >
              <FaTrash className="w-2.5 h-2.5" /> Remove Setup
            </button>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="p-3 bg-white dark:bg-neutral-900 rounded-xl shadow-sm border border-gray-100 dark:border-neutral-700">
              <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">State</p>
              <div className="text-sm font-bold text-gray-900 dark:text-white">{selectedMenuUser.annualReportService.state}</div>
            </div>
            <div className="p-3 bg-white dark:bg-neutral-900 rounded-xl shadow-sm border border-gray-100 dark:border-neutral-700">
              <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">Entity Type</p>
              <div className="text-sm font-bold text-gray-900 dark:text-white">{selectedMenuUser.annualReportService.entityType}</div>
            </div>
            <div className="p-3 bg-white dark:bg-neutral-900 rounded-xl shadow-sm border border-gray-100 dark:border-neutral-700">
              <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">Filing Year</p>
              <div className="text-sm font-bold text-gray-900 dark:text-white">{selectedMenuUser.annualReportService.filingYear || 'N/A'}</div>
            </div>
          </div>
          <div className="p-4 bg-white dark:bg-neutral-900 rounded-xl border border-gray-100 dark:border-neutral-700">
            <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase mb-2">Due Date Display</p>
            <div className="text-red-600 font-black text-lg">
              {selectedMenuUser.annualReportService.dueDateText || formatDateForDisplay(selectedMenuUser.annualReportService.dueDate)}
            </div>
            <button
              type="button"
              onClick={() => handleSendAnnualReportReminder(selectedMenuUser._id)}
              disabled={manualReminderLoading}
              className="w-full mt-3 flex items-center justify-center gap-2 py-2 bg-primary-50 hover:bg-primary-100 text-primary-600 text-[10px] font-bold rounded-lg transition-all"
            >
              {manualReminderLoading ? <div className="w-3 h-3 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" /> : <FaBell />}
              Send Instant Reminder
            </button>
            {renderReminderTracking(
              selectedMenuUser.annualReportService.oneMonthReminderSent,
              selectedMenuUser.annualReportService.fifteenDayReminderSent
            )}
          </div>
        </div>
      )}

      {selectedMenuUser?.annualReportService?.dueDate && (
        <div className="space-y-3">
          <h5 className="font-bold text-gray-900 dark:text-white">Annual Report Payment / Lock</h5>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
                Payment Status
              </label>
              <select
                value={
                  selectedMenuUser?.annualReportPaymentStatus
                  || (selectedMenuUser?.annualReportLocked ? 'unpaid' : 'paid')
                }
                onChange={(e) => {
                  if (typeof handleAnnualReportPaymentStatusUpdate === 'function') {
                    handleAnnualReportPaymentStatusUpdate(selectedMenuUser._id, e.target.value);
                  }
                }}
                className={formSelectClass}
              >
                <option value="paid">Paid (Unlocked)</option>
                <option value="unpaid">Unpaid (Locked)</option>
              </select>
            </div>
            <div className={`p-4 rounded-xl border flex items-center ${
              selectedMenuUser?.annualReportLocked
                ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
                : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            }`}>
              <div className={`text-sm font-bold ${selectedMenuUser?.annualReportLocked ? 'text-orange-700 dark:text-orange-300' : 'text-green-700 dark:text-green-300'}`}>
                {selectedMenuUser?.annualReportLocked ? 'Locked — awaiting payment' : 'Unlocked / Active'}
              </div>
            </div>
          </div>
        </div>
      )}

      {['Florida', 'Wyoming', 'New Jersey', 'Delaware'].includes(selectedMenuUser?.annualReportService?.state) && (
        <div className="mt-8 pt-6 border-t border-gray-100 dark:border-neutral-700">
          <h5 className="font-bold text-gray-900 dark:text-white mb-4">Annual Report Workflow Management</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`p-4 rounded-xl border-2 ${
              selectedMenuUser?.annualReportSteps?.find((s) => s.step === 1)?.status === 'submitted'
                ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                : 'bg-gray-50 dark:bg-neutral-900/50 border-gray-200 dark:border-neutral-700'
            }`}>
              <h6 className="font-bold text-gray-900 dark:text-white text-sm mb-1">
                {selectedMenuUser?.annualReportService?.state} Initial Information Required
              </h6>
              {selectedMenuUser?.annualReportSteps?.find((s) => s.step === 1)?.status === 'submitted' && (
                <div className="flex flex-col gap-2 mt-3">
                  {selectedMenuUser?.annualReportSteps?.find((s) => s.step === 1)?.pdfUrl && (
                    <a
                      href={`${process.env.REACT_APP_API_BASE_URL}${selectedMenuUser.annualReportSteps.find((s) => s.step === 1).pdfUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-primary-600 hover:text-primary-700"
                    >
                      <FaFilePdf /> View Submitted PDF
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => handleReopenAnnualReportChecklist(selectedMenuUser._id)}
                    className="w-full py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-700 text-[10px] font-bold rounded-lg flex items-center justify-center gap-1.5"
                  >
                    <FaUnlock className="w-3 h-3" /> Re-open for User
                  </button>
                </div>
              )}
            </div>
            {(() => {
              const step2RawStatus = selectedMenuUser?.annualReportSteps?.find((s) => s.step === 2)?.status || 'pending';
              const step1Submitted = selectedMenuUser?.annualReportSteps?.find((s) => s.step === 1)?.status === 'submitted';
              const step2Status = step1Submitted && step2RawStatus !== 'submitted'
                ? (step2RawStatus === 'pending' ? 'in-progress' : step2RawStatus)
                : step2RawStatus;
              const step2CardClass = step2Status === 'submitted'
                ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                : step2Status === 'in-progress'
                ? 'bg-slate-100 dark:bg-slate-900/40 border-slate-300 dark:border-slate-600'
                : 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800';
              return (
            <div className={`p-4 rounded-xl border-2 ${step2CardClass}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-gray-500 uppercase">Step 2</span>
                {step2Status === 'submitted' ? (
                  <span className="px-2 py-0.5 bg-green-500 text-white text-[10px] font-bold rounded-full uppercase">Completed</span>
                ) : step2Status === 'in-progress' ? (
                  <span className="px-2 py-0.5 bg-slate-600 text-white text-[10px] font-bold rounded-full uppercase">In Progress</span>
                ) : (
                  <span className="px-2 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full uppercase">Pending</span>
                )}
              </div>
              <h6 className="font-bold text-gray-900 dark:text-white text-sm mb-1">Annual Report Filed</h6>
              <p className="text-[10px] text-gray-500 mb-3">Admin review and filing completion.</p>
              <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">Status</label>
              <select
                className={formSelectClass}
                value={step2Status}
                onChange={(e) => handleAnnualReportStepStatusChange(selectedMenuUser._id, 2, e.target.value)}
                disabled={!step1Submitted}
              >
                {step1Submitted ? (
                  <>
                    <option value="in-progress">In Progress</option>
                    <option value="submitted">Completed</option>
                  </>
                ) : (
                  <option value="pending">Pending</option>
                )}
              </select>
              {!step1Submitted && (
                <p className="text-[10px] text-gray-400 mt-2">Available after Step 1 is submitted.</p>
              )}
              {step1Submitted && (
                <p className="text-[10px] text-gray-400 mt-2">User sees In Progress until marked Completed.</p>
              )}
            </div>
              );
            })()}
          </div>
        </div>
      )}

      {Array.isArray(selectedMenuUser?.annualReportHistory) && selectedMenuUser.annualReportHistory.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-100 dark:border-neutral-700 space-y-3">
          <h5 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FaHistory className="text-gray-500" /> Previous Filing History ({selectedMenuUser.annualReportHistory.length})
          </h5>
          {selectedMenuUser.annualReportHistory.map((historyItem, historyIndex) => (
            <div
              key={`ar-emp-history-${historyIndex}-${historyItem.filingYear || historyIndex}`}
              className="rounded-xl border border-gray-200 dark:border-neutral-700 overflow-hidden"
            >
              <div className="px-4 py-3 bg-gray-50 dark:bg-neutral-800/80 flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    Filing Year {historyItem.filingYear || 'N/A'}
                  </span>
                  {historyItem.entityType && (
                    <span className="px-2 py-0.5 rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-semibold">
                      {historyItem.entityType}
                    </span>
                  )}
                  {historyItem.state && (
                    <span className="px-2 py-0.5 rounded-lg bg-slate-100 dark:bg-neutral-700 text-slate-600 dark:text-slate-300 text-xs font-semibold">
                      {historyItem.state}
                    </span>
                  )}
                </div>
                <span className="text-xs font-semibold text-green-600 dark:text-green-400">✓ Completed</span>
              </div>
              <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(historyItem.steps || []).map((s) => (
                  <div
                    key={`ar-emp-history-${historyIndex}-step-${s.step}`}
                    className="p-3 rounded-lg bg-green-500 text-white text-center min-h-[72px] flex flex-col justify-center"
                  >
                    <div className="text-[10px] font-bold uppercase opacity-80">Step {s.step}</div>
                    <div className="text-xs font-semibold">{s.title}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnnualReportTab;
